import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import type { ThunkDispatch } from "redux-thunk";

import { useDisableBodyScroll } from "../../hooks/useDisableBodyScroll";
import { accountUsersActions } from "../../reducers/accountUsers";
import type {
  GeneralUserData,
  AccountData,
} from "../../types/generalUserManage";
import { constrainCreditLimit } from "../../utils/creditLimitUtils";
import {
  validateUsernamePassword,
  validateEmail,
  validateRequired,
} from "../../utils/validators";

import CreditLimitAdjustButtons from "./CreditLimitAdjustButtons";

interface UserInfoModalProps {
  onClose: () => void;
  isCreate: boolean;
  userData?: GeneralUserData;
  currentUserId?: number;
  accountData: AccountData | null;
  users: GeneralUserData[];
}

interface UpdateUserData {
  account_id: number;
  user_id: number;
  user_name: string;
  full_name: string;
  email: string;
  role_type: "Admin" | "General" | "Viewer";
  credit_limit: number | null;
  password?: string;
}

const UserInfoModal: React.FC<UserInfoModalProps> = ({
  onClose,
  isCreate,
  userData,
  currentUserId,
  accountData,
  users,
}) => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, unknown>>();

  // モーダル表示時に背景のスクロールを無効化
  useDisableBodyScroll();

  // 自分自身を編集しているかチェック
  const isEditingSelf = !isCreate && userData?.user_id === currentUserId;

  // 割り振り可能なクレジット数を計算
  const availableCredit = useMemo(() => {
    if (!accountData) return 0;

    // is_activeがtrueのユーザーのcredit_limitの合計
    const activeUsersTotal = users
      .filter(
        (user) =>
          user.is_active && (!userData || user.user_id !== userData.user_id),
      ) // 編集時は自分自身を除外
      .reduce((sum, user) => sum + (user.credit_limit || 0), 0);

    // アカウント上限 - 現在の合計
    return accountData.credit_limit - activeUsersTotal;
  }, [accountData, users, userData]);

  const [fullName, setFullName] = useState(""); // ユーザー名
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState(""); // ログインID
  const [password, setPassword] = useState("");
  const [roleType, setRoleType] = useState("General");
  const [creditLimit, setCreditLimit] = useState("");

  // バリデーションエラー用のstate
  const [errors, setErrors] = useState({
    userName: "",
    password: "",
    fullName: "",
    email: "",
  });

  useEffect(() => {
    if (isCreate) {
      setUserName("");
      setPassword("");
      setFullName("");
      setEmail("");
      setRoleType("General");
      setCreditLimit("");
      setErrors({ userName: "", password: "", fullName: "", email: "" });
    } else if (userData) {
      setUserName(userData.user_name || "");
      setPassword("");
      setFullName(userData.full_name || "");
      setEmail(userData.email || "");
      setRoleType(userData.role_type || "General");
      setCreditLimit(
        userData.credit_limit === null ? "" : userData.credit_limit.toString(),
      );
      setErrors({ userName: "", password: "", fullName: "", email: "" });
    }
  }, [isCreate, userData]);

  const validateForm = (): boolean => {
    const newErrors = {
      userName: "",
      password: "",
      fullName: "",
      email: "",
    };

    // ログインIDのバリデーション
    const userNameRequiredError = validateRequired(userName, "ログインID");
    if (userNameRequiredError) {
      newErrors.userName = userNameRequiredError;
    } else {
      newErrors.userName = validateUsernamePassword(userName, "ログインID");
    }

    // パスワードのバリデーション
    if (isCreate) {
      // 新規作成時は必須
      const passwordRequiredError = validateRequired(password, "パスワード");
      if (passwordRequiredError) {
        newErrors.password = passwordRequiredError;
      } else {
        newErrors.password = validateUsernamePassword(password, "パスワード");
      }
    } else if (password.trim()) {
      // 編集時にパスワードが入力されている場合のみバリデーション
      newErrors.password = validateUsernamePassword(password, "パスワード");
    }

    // ユーザー名のバリデーション
    newErrors.fullName = validateRequired(fullName, "ユーザー名");

    // メールアドレスのバリデーション
    newErrors.email = validateEmail(email);

    setErrors(newErrors);

    // エラーがあるかチェック
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleCreditLimitChange = (value: string) => {
    setCreditLimit(constrainCreditLimit(value, availableCredit));
  };

  const handleSubmit = async () => {
    // バリデーション実行
    if (!validateForm()) {
      return;
    }

    if (isCreate) {
      // 新規作成
      const result = await dispatch(
        accountUsersActions.createUser({
          account_id: accountData!.id,
          user_name: userName,
          password,
          full_name: fullName,
          email,
          role_type: roleType as "Admin" | "General" | "Viewer",
          credit_limit: creditLimit ? parseInt(creditLimit, 10) : null,
        }),
      );

      if (accountUsersActions.createUser.fulfilled.match(result)) {
        // 成功した場合のみモーダルを閉じる
        onClose();
      }
    } else {
      // 編集処理
      if (!userData?.user_id) {
        return;
      }

      const updated: UpdateUserData = {
        account_id: accountData!.id,
        user_id: userData.user_id,
        user_name: userName,
        full_name: fullName,
        email,
        role_type: roleType as "Admin" | "General" | "Viewer",
        credit_limit: creditLimit ? parseInt(creditLimit, 10) : null,
      };

      // パスワードが入力された場合のみ含める
      if (password) {
        updated.password = password;
      }

      const result = await dispatch(
        accountUsersActions.updateUser({
          current: userData,
          updated,
        }),
      );

      if (accountUsersActions.updateUser.fulfilled.match(result)) {
        // 成功した場合のみモーダルを閉じる
        onClose();
      }
    }
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center">
      {/* 背景オーバーレイ */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      />

      {/* モーダル本体 */}
      <div className="relative bg-white rounded-lg shadow-lg w-full max-w-2xl">
        {/* タイトルバー */}
        <div className="bg-[#5571ad] text-white px-6 py-3 rounded-t-lg">
          <h3 className="text-2xl font-bold">
            {isCreate ? "ユーザー登録" : "ユーザー編集"}
          </h3>
        </div>

        {/* 入力フォーム */}
        <div className="">
          <form autoComplete="off">
            <table className="w-full">
              <tbody>
                {/* ユーザーID（編集時のみ） */}
                {!isCreate && (
                  <tr className="border-b">
                    <td
                      className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                      style={{ minWidth: "160px" }}
                    >
                      No
                    </td>
                    <td className="px-4 py-3">
                      {userData?.employee_id
                        ? String(userData.employee_id).padStart(5, "0")
                        : "-"}
                    </td>
                  </tr>
                )}

                {/* ログインID */}
                <tr className="border-b">
                  <td
                    className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                    style={{ minWidth: "160px" }}
                  >
                    ログインID<span style={{ color: "#ef4444" }}> *</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        placeholder="半角英数字（a-z, A-Z, 0-9）、記号（. _ - @）"
                        maxLength={64}
                        autoComplete="username"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p
                        className="absolute text-xs text-gray-400 pointer-events-none"
                        style={{ right: "0.2rem", bottom: "-0.9rem" }}
                      >
                        {userName.length}/64
                      </p>
                    </div>
                    {errors.userName && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.userName}
                      </p>
                    )}
                  </td>
                </tr>

                {/* パスワード（作成時の場合のみ） */}
                {isCreate ? (
                  <tr className="border-b">
                    <td
                      className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                      style={{ minWidth: "160px" }}
                    >
                      パスワード<span style={{ color: "#ef4444" }}> *</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="半角英数字（a-z, A-Z, 0-9）、記号（. _ - @）"
                          maxLength={64}
                          autoComplete="new-password" // これを設定しないとGoogle Chromeにて自動的にパスワードが入力される
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p
                          className="absolute text-xs text-gray-400 pointer-events-none"
                          style={{ right: "0.2rem", bottom: "-0.9rem" }}
                        >
                          {password.length}/64
                        </p>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.password}
                        </p>
                      )}
                    </td>
                  </tr>
                ) : null}

                {/* ユーザー名 */}
                <tr className="border-b">
                  <td
                    className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                    style={{ minWidth: "160px" }}
                  >
                    ユーザー名<span style={{ color: "#ef4444" }}> *</span>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder={isCreate ? "店舗名または個人名" : ""}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.fullName && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.fullName}
                      </p>
                    )}
                    {isCreate && !errors.fullName && (
                      <p className="text-sm text-gray-500 mt-1">
                        例：「渋谷店」「田中太郎」
                      </p>
                    )}
                  </td>
                </tr>

                {/* メールアドレス */}
                <tr className="border-b">
                  <td
                    className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                    style={{ minWidth: "160px" }}
                  >
                    メールアドレス<span style={{ color: "#ef4444" }}> *</span>
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </td>
                </tr>

                {/* 権限 */}
                <tr className="border-b">
                  <td
                    className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                    style={{ minWidth: "160px" }}
                  >
                    権限
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={roleType}
                      onChange={(e) => setRoleType(e.target.value)}
                      disabled={isEditingSelf}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isEditingSelf
                          ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed"
                          : "border-gray-300"
                      }`}
                    >
                      <option value="General">
                        一般ユーザー （らくデコ機能のみ利用可能）
                      </option>
                      <option value="Viewer">
                        閲覧ユーザー （管理者画面のみ閲覧可能）
                      </option>
                      <option value="Admin">
                        管理者ユーザー （らくデコ機能・管理者画面を利用可能）
                      </option>
                    </select>
                    {isEditingSelf && (
                      <p className="text-sm text-gray-400 mt-1">
                        ご自身の権限は変更できません
                      </p>
                    )}
                  </td>
                </tr>

                {/* 個別上限(今月) */}
                <tr className="border-b">
                  <td
                    className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                    style={{ minWidth: "160px" }}
                  >
                    個別上限(今月)
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) =>
                          handleCreditLimitChange(e.target.value)
                        }
                        placeholder="1000"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <CreditLimitAdjustButtons
                        currentValue={creditLimit}
                        onChange={handleCreditLimitChange}
                      />
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {availableCredit.toLocaleString()}
                      クレジット割振可能（空欄の場合は上限なし）
                    </p>
                  </td>
                </tr>

                {/* パスワード変更（編集時のみ） */}
                {!isCreate && (
                  <tr className="border-b">
                    <td
                      className="bg-[#778ebe] text-white font-bold text-center px-4 py-3"
                      style={{ minWidth: "160px" }}
                    >
                      パスワード変更
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="半角英数字（a-z, A-Z, 0-9）、記号（. _ - @）※変更時のみ"
                          maxLength={64}
                          autoComplete="new-password"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p
                          className="absolute text-xs text-gray-400 pointer-events-none"
                          style={{ right: "0.2rem", bottom: "-0.9rem" }}
                        >
                          {password.length}/64
                        </p>
                      </div>
                      {errors.password && (
                        <p className="text-sm text-red-600 mt-1">
                          {errors.password}
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </form>
        </div>

        {/* ボトムバー */}
        <div className="flex justify-end gap-3 px-6 py-3 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            {isCreate ? "登録" : "更新"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserInfoModal;
